import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  IconMultiCheckBlueCircle,
  IconMultiCheckGreyCircle,
  IllustrationLogoSnoroseCloud,
} from '@snorose/icons';

import {
  BackAppBar,
  NewButton,
  PasswordInput,
  TextInput,
} from '@/shared/component';
import { TOAST } from '@/shared/constant';
import { useToast } from '@/shared/hook';

import { useLogin } from '@/feature/auth/hooks';
import {
  getMaintenanceAccess,
  subscribeMaintenanceAccess,
} from '@/feature/maintenance/access';
import { isMaintenanceTime } from '@/feature/maintenance/config';

import { MaintenancePage } from '@/page/maintenance';

import styles from './LoginPage.module.css';

const LOGIN_ERROR_MAP = {
  '아이디 또는 비밀번호가 틀립니다.': '아이디 혹은 비밀번호가 일치하지 않아요',
};

export default function Login() {
  // 점검 시작·종료 시 기존 리자의 로그인 화면도 다시 렌더링합니다.
  useSyncExternalStore(subscribeMaintenanceAccess, getMaintenanceAccess);
  const location = useLocation();
  const redirectTo = getRedirectPath(location.state);

  const [formData, setFormData] = useState({ loginId: '', password: '' });
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRememberId, setIsRememberId] = useState(false);

  const { toast } = useToast();
  const { mutate: login } = useLogin({
    redirectTo,
    onError: (error) => {
      const status = error.response?.status;
      const serverMsg =
        error.response?.data?.message || '네트워크 연결 상태를 확인해주세요.';

      if (status === 500) {
        toast.error(TOAST.ERROR.SERVER);
      } else {
        setErrorMessage(LOGIN_ERROR_MAP[serverMsg] ?? serverMsg);
      }

      setIsError(true);
    },
  });

  useEffect(() => {
    const savedId = localStorage.getItem('rememberedLoginId');

    if (savedId) {
      setFormData((prev) => ({ ...prev, loginId: savedId }));
      setIsRememberId(true);
    }
  }, []);

  const handleLoginSubmit = (e) => {
    e.preventDefault();

    if (!formData.loginId) {
      toast.info(TOAST.LOGIN.emptyId);
      return;
    }

    if (!formData.password) {
      toast.info(TOAST.LOGIN.emptyPw);
      return;
    }

    if (isRememberId) {
      localStorage.setItem('rememberedLoginId', formData.loginId);
    } else {
      localStorage.removeItem('rememberedLoginId');
    }

    login(formData);
  };

  const inputProps = [
    {
      type: 'text',
      id: 'loginId',
      placeholder: '아이디',
      value: formData.loginId,
      onChange: (next) => setFormData((prev) => ({ ...prev, loginId: next })),
    },
    {
      type: 'password',
      id: 'password',
      placeholder: '영어, 숫자, 특수문자를 포함한 비밀번호',
      value: formData.password,
      onChange: (next) => setFormData((prev) => ({ ...prev, password: next })),
    },
  ];

  if (isMaintenanceTime()) return <MaintenancePage />;

  return (
    <div className={styles.container}>
      <BackAppBar />

      <form onSubmit={handleLoginSubmit}>
        <IllustrationLogoSnoroseCloud
          className={styles.logo}
          role='img'
          aria-label='스노로즈 로고'
        />

        <p className={styles.title}>
          숙명인을 위한 커뮤니티,
          <br />
          스노로즈에 오신 것을 환영합니다!
        </p>

        <div className={styles.form}>
          {inputProps.map((props) => {
            const Input = {
              text: TextInput,
              password: PasswordInput,
            }[props.type];

            let status = isError ? 'error' : 'default';
            status = props.value === '' ? 'default' : status;
            return <Input status={status} {...props} />;
          })}
        </div>

        {errorMessage && (
          <p role='alert' className={styles.errorMessage}>
            {errorMessage}
          </p>
        )}

        <div
          className={styles.rememberIdCheckbox}
          onClick={() => setIsRememberId((prev) => !prev)}
        >
          {isRememberId ? (
            <IconMultiCheckBlueCircle width={22} height={22} />
          ) : (
            <IconMultiCheckGreyCircle width={22} height={22} />
          )}
          <span>아이디 기억하기</span>
        </div>

        <NewButton>로그인하기</NewButton>

        <div className={styles.find}>
          <Link to='/signup'>회원가입하기</Link>
          <p className={styles.divider}>|</p>
          <Link to='/find-id'>아이디 찾기</Link>
          <p className={styles.divider}>|</p>
          <Link to='/find-pw'>비밀번호 찾기</Link>
        </div>
      </form>
    </div>
  );
}

function getRedirectPath(state) {
  const from = state?.from;
  const pathname = from?.pathname;

  if (
    typeof pathname !== 'string' ||
    !pathname.startsWith('/') ||
    pathname.startsWith('//') ||
    pathname === '/login'
  ) {
    return '/';
  }

  const search = typeof from.search === 'string' ? from.search : '';
  const hash = typeof from.hash === 'string' ? from.hash : '';

  return `${pathname}${search}${hash}`;
}
